import React, { useState, useEffect } from 'react';
import { usePermissions } from '../../hooks/usePermissions';
import { useInvoicePermissions } from '../../hooks/useInvoicePermissions';
import { useSpecializedVoucherPermissions } from '../../hooks/useSpecializedVoucherPermissions';
import {
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  ListItemButton,
  Divider,
  Box,
  Button,
  Typography,
  Paper,
  Chip,
  alpha,
  Avatar,
  Badge,
  Collapse,
  Tooltip,
  useTheme,
  CircularProgress
} from '@mui/material';
import { styled } from '@mui/material/styles';
import { NavLink, NavLinkProps, useLocation } from 'react-router-dom';
import {
  Person as PersonIcon,
  LocalShipping as SupplierIcon,
  ExpandLess as ExpandLessIcon,
  ExpandMore as ExpandMoreIcon,
  People as PeopleIcon,
  Settings as SettingsIcon,
  Business as BusinessIcon,
  Warehouse as WarehouseIcon,
  List as ListIcon,
  Inventory as InventoryIcon,
  Add as AddIcon,
  Dashboard as DashboardIcon,
  SupervisorAccount as AdminIcon,
  LayersClear as LayersClearIcon,
  StraightenOutlined,
  Category as CategoryIcon,
  Group as GroupIcon,
  VerifiedUser as VerifiedUserIcon,
  SwapHoriz as SwapHorizIcon,
  Style as StyleIcon,
  AccountBalance as AccountBalanceIcon,
  Receipt as ReceiptIcon,
  LibraryBooks as LibraryBooksIcon,
  ImportExport as ImportExportIcon,
  Paid as PaidIcon,
  Payment as PaymentIcon,
  BarChart as BarChartIcon,
  Assessment as AssessmentIcon,
  AttachMoney as AttachMoneyIcon,
  LocalAtm as LocalAtmIcon,
  ReceiptLong as ReceiptLongIcon,
  Description as DescriptionIcon,
  Settings as ConfigIcon,
  ShoppingCart as SalesIcon,
  ShoppingCart as ShoppingCartIcon,
  AddShoppingCart as PurchaseIcon,
  RequestQuote as QuoteIcon,
  AssignmentTurnedIn as AssignmentTurnedInIcon,
  FindInPage as FindInPageIcon,
  Campaign as CampaignIcon,
  LocalOffer as OfferIcon,
  AddCircle as AddCampaignIcon,
  CardGiftcard as CouponIcon,
  Visibility as VisibilityIcon,
  Refresh as RefreshIcon,
  Security as SecurityIcon,
  Sms as SmsIcon,
  Send as SendIcon,
  TrendingUp as TrendingUpIcon,
  Notifications as NotificationsIcon,
  WhatsApp as WhatsAppIcon,
  History as HistoryIcon,
  BarChart as AnalyticsIcon,
  Stars as StarsIcon,
  AdminPanelSettings as AdminPanelSettingsIcon,
  Event as EventIcon
} from '@mui/icons-material';
import { getInvoiceMenuItems } from './DynamicInvoiceMenu';
import { getSpecializedVoucherMenuItems } from './DynamicSpecializedVoucherMenu';

// Animation for menu items
const StyledListItem = styled(ListItem)(({ theme }) => ({
  transition: 'all 0.25s ease',
  position: 'relative',
  overflow: 'hidden',
  '&::after': {
    content: '""',
    position: 'absolute',
    bottom: 0,
    left: '100%',
    width: '100%',
    height: 3,
    borderRadius: '0 0 4px 4px',
    backgroundColor: theme.palette.primary.main,
    transition: 'left 0.3s ease',
  },
  '&:hover::after': {
    left: 0,
  }
}));

// Logo component for the sidebar
const Logo = styled(Box)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  padding: theme.spacing(2),
  transition: 'transform 0.3s ease',
  cursor: 'pointer',
  '&:hover': {
    transform: 'scale(1.05)',
  }
}));

// Custom styled NavLink with updated styling for RTL
const StyledNavLink = React.forwardRef<HTMLAnchorElement, NavLinkProps>((props, ref) => (
  <NavLink
    {...props}
    ref={ref}
    style={({ isActive }) => ({
      color: isActive ? '#30c3b0' : 'inherit',
      textDecoration: 'none',
      backgroundColor: isActive ? 'rgba(48, 195, 176, 0.1)' : 'transparent',
      fontWeight: isActive ? 'bold' : 'normal',
      width: '100%',
      display: 'flex'
    })}
  />
));

// Wrapper for section headers
const SectionHeader = styled(Box)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  padding: theme.spacing(0, 2),
  marginTop: theme.spacing(2),
  marginBottom: theme.spacing(1),
}));

interface MenuItemProps {
  to: string;
  icon: React.ReactNode;
  text: string;
  badge?: number;
  sectionId?: string;
  pageId?: string;
  actionId?: string;
  hideIfNoPermission?: boolean;
  permissionCheck?: {
    section: string;
    page: string;
    action: string;
  };
}

const MenuItem: React.FC<MenuItemProps> = ({
  to,
  icon,
  text,
  badge,
  sectionId,
  pageId,
  actionId = 'view',
  hideIfNoPermission = false,
  permissionCheck
}) => {
  const location = useLocation();
  const isActive = location.pathname === to ||
                  (to !== '/' && location.pathname.startsWith(to));
  const theme = useTheme();
  const { hasPermission } = usePermissions();
  const { hasInvoicePagePermission, hasInvoiceSettingPermission } = useInvoicePermissions();
  
  // التحقق من الصلاحية
  let hasAccessPermission = true;
  
  // استخدام التحقق المخصص لقسم الفواتير إذا كان متوفرًا
  if (permissionCheck) {
    const { section, page, action } = permissionCheck;
    
    // إذا كان القسم هو الفواتير
    if (section === 'invoices') {
      // التحقق مما إذا كانت الصفحة ديناميكية (صفحة فاتورة)
      if (page.startsWith('page-')) {
        const pageId = parseInt(page.replace('page-', ''));
        hasAccessPermission = hasInvoicePagePermission(pageId, action);
      } else {
        // صفحة ثابتة مثل الإعدادات أو قوالب الطباعة
        hasAccessPermission = hasInvoiceSettingPermission(page, action);
      }
    }
  } else if (sectionId && pageId) {
    // استخدام نظام الصلاحيات العام للأقسام الأخرى
    hasAccessPermission = hasPermission(sectionId, pageId, actionId);
  }
  
  // إذا لم تكن هناك صلاحية ونريد إخفاء العنصر
  if (!hasAccessPermission && (hideIfNoPermission || permissionCheck)) {
    return null;
  }
  
  return (
    <StyledListItem disablePadding sx={{ mb: { xs: 0.5, md: 0.8 } }}>
      <ListItemButton
        component={StyledNavLink}
        to={to}
        sx={{
          py: { xs: 1, md: 1.2 },
          px: { xs: 1.5, md: 2 },
          borderRadius: 2,
          transition: 'all 0.2s ease-in-out',
          minHeight: { xs: 44, md: 48 }, // حد أدنى لارتفاع العنصر للجوال
          '&:hover': {
            backgroundColor: alpha(theme.palette.primary.main, 0.08),
            transform: 'translateY(-2px)',
            boxShadow: isActive ? '0 4px 10px rgba(48, 195, 176, 0.15)' : 'none'
          },
          backgroundColor: isActive ? alpha(theme.palette.primary.main, 0.1) : 'transparent',
          boxShadow: isActive ? '0 4px 8px rgba(48, 195, 176, 0.1)' : 'none',
        }}
      >
        <ListItemIcon sx={{
          minWidth: { xs: 32, md: 36 },
          color: isActive ? '#30c3b0' : alpha(theme.palette.text.primary, 0.6),
          transition: 'color 0.2s ease',
          '& svg': {
            fontSize: { xs: '1.2rem', md: '1.5rem' }
          }
        }}>
          {icon}
        </ListItemIcon>
        <ListItemText
          primary={text}
          primaryTypographyProps={{
            // خط أصغر للقائمة الفرعية مقارنة برأس القسم الرئيسي
            fontSize: { xs: 11.5, md: 12.5 },
            fontWeight: isActive ? 600 : 500,
            color: isActive ? '#30c3b0' : theme.palette.text.primary,
            textAlign: 'left'
          }}
          sx={{ transition: 'color 0.2s ease' }}
        />
        {badge && (
          <Badge
            badgeContent={badge}
            color="success"
            sx={{
              '& .MuiBadge-badge': {
                right: -3,
                top: 8,
                border: '2px solid white',
                padding: '0 4px',
                minWidth: 18,
                height: 18,
                fontSize: '0.65rem',
                fontWeight: 'bold'
              }
            }}
          />
        )}
      </ListItemButton>
    </StyledListItem>
  );
};

// Collapsible section component
interface CollapsibleSectionProps {
  title: string;
  icon: React.ReactNode;
  children: React.ReactNode;
  defaultOpen?: boolean;
  badgeCount?: number;
  sectionId?: string;
  hideIfNoPermission?: boolean;
}

const CollapsibleSection: React.FC<CollapsibleSectionProps> = ({
  title,
  icon,
  children,
  defaultOpen = false,
  badgeCount,
  sectionId,
  hideIfNoPermission = false
}) => {
  const [isOpen, setIsOpen] = React.useState(defaultOpen);
  const theme = useTheme();
  const { hasPermission } = usePermissions();
  const { hasInvoicePagePermission, hasInvoiceSettingPermission } = useInvoicePermissions();

  // التحقق من وجود صلاحيات للقسم
  const hasSectionAccess = React.useMemo(() => {
    if (!hideIfNoPermission || !sectionId) {
      return true;
    }

    // معالجة خاصة لقسم الفواتير
    if (sectionId === 'invoices') {
      // التحقق من الصفحات الثابتة
      const staticPages = ['settings', 'print-templates', 'product-barcode', 'reports'];
      const hasStaticPermission = staticPages.some(pageId =>
        hasInvoiceSettingPermission(pageId, 'view') ||
        hasInvoiceSettingPermission(pageId, 'view_page')
      );
      
      // التحقق من وجود أي صفحة فواتير ديناميكية
      // نتحقق من عدة صفحات محتملة (من 1 إلى 20) لضمان اكتشاف أي صلاحية
      let hasDynamicPermission = false;
      for (let i = 1; i <= 20; i++) {
        if (hasInvoicePagePermission(i, 'view_page') ||
            hasInvoicePagePermission(i, 'view') ||
            hasPermission('invoices', `page-${i}`, 'view_page') ||
            hasPermission('invoices', `page-${i}`, 'view')) {
          hasDynamicPermission = true;
          break;
        }
      }
      
      // إذا لم يكن لديه صلاحية ثابتة أو ديناميكية، نتحقق من القسم نفسه
      if (!hasStaticPermission && !hasDynamicPermission) {
        // التحقق من صلاحية القسم العامة
        hasDynamicPermission = hasPermission('invoices', 'all', 'view_page') ||
                              hasPermission('invoices', 'all', 'view');
      }
      
      return hasStaticPermission || hasDynamicPermission;
    }

    // معالجة خاصة لقسم الحسابات المالية للتحقق من صلاحيات السندات المتخصصة الديناميكية
    if (sectionId === 'finance') {
      const staticPages = ['accounts-management', 'account-statements', 'general-ledger', 'receipt-vouchers', 'payment-vouchers', 'specialized-voucher-templates', 'fee-groups', 'financial-reports', 'balance-management', 'export-import'];
      const hasStaticPermission = staticPages.some(pageId =>
        hasPermission('finance', pageId, 'view') ||
        hasPermission('finance', pageId, 'view_page')
      );

      // التحقق من صلاحيات السندات المتخصصة الديناميكية (voucher-*)
      let hasDynamicVoucherPermission = false;
      // نتحقق من معرفات قوالب شائعة (1 إلى 50) لضمان اكتشاف أي صلاحية سند متخصص
      for (let i = 1; i <= 50; i++) {
        if (hasPermission('finance', `voucher-${i}`, 'view_page') ||
            hasPermission('finance', `voucher-${i}`, 'view')) {
          hasDynamicVoucherPermission = true;
          break;
        }
      }

      // التحقق من صلاحية عامة للسندات المتخصصة
      if (!hasDynamicVoucherPermission) {
        hasDynamicVoucherPermission = hasPermission('finance', 'specialized-vouchers', 'view_page') ||
                                     hasPermission('finance', 'specialized-vouchers', 'view');
      }

      return hasStaticPermission || hasDynamicVoucherPermission;
    }

    // تحديد الصفحات المناسبة للأقسام الأخرى
    const sectionPages: { [key: string]: string[] } = {
      'contacts': ['all', 'customers', 'suppliers', 'linked-accounts', 'import', 'loyalty-points', 'all-loyalty-points'],
      'products': ['all', 'categories', 'attributes', 'service-products', 'service-product-operations', 'add-new', 'import', 'bulk-update', 'units', 'reorder-quantities'],
      'marketing': ['dashboard', 'campaigns', 'new-campaign', 'coupons', 'coupon-storage', 'sms-send', 'sms-history', 'sms-providers', 'whatsapp-providers', 'whatsapp-send', 'whatsapp-campaigns', 'whatsapp-history', 'whatsapp-analytics'],
      'warehouses': ['list', 'transfers', 'new-transfer', 'inventory-events'],
      'inventory-count': ['list', 'new-request'],
      'warehouse-transfer-requests': ['list', 'new-request'],
      'finance': ['accounts-management', 'account-statements', 'general-ledger', 'receipt-vouchers', 'payment-vouchers', 'specialized-voucher-templates', 'fee-groups', 'financial-reports', 'balance-management', 'export-import'],
      'reports': ['inventory', 'inventory-movement', 'product-movement-report', 'product-profit-report', 'product-prices', 'category-quantities-report', 'reorder-quantities-report', 'zatca-invoice-report', 'payment-receipt-vouchers-report', 'specialized-vouchers-report', 'inventory-display-report', 'sales-commissions'],
      'settings': ['branches', 'taxes', 'zatca-certificates', 'document_alerts', 'advanced-settings', 'dashboard-widgets'],
      'admin': ['users', 'user-groups']
    };

    const pagesToCheck = sectionPages[sectionId] || ['all', 'list', 'view_page', 'dashboard', 'management'];
    
    // التحقق من وجود صلاحية واحدة على الأقل في القسم
    return pagesToCheck.some(pageId =>
      hasPermission(sectionId, pageId, 'view') ||
      hasPermission(sectionId, pageId, 'view_page')
    );
  }, [sectionId, hideIfNoPermission, hasPermission, hasInvoicePagePermission, hasInvoiceSettingPermission]);

  // إخفاء القسم إذا لم تكن هناك صلاحيات
  if (!hasSectionAccess) {
    return null;
  }

  return (
    <>
      <StyledListItem disablePadding sx={{ mb: { xs: 0.3, md: 0.5 } }}>
        <ListItemButton
          onClick={() => setIsOpen(!isOpen)}
          sx={{
            py: { xs: 1.2, md: 1.5 },
            px: { xs: 1.5, md: 2 },
            borderRadius: 2,
            minHeight: { xs: 48, md: 52 },
            transition: 'all 0.2s ease',
            '&:hover': {
              backgroundColor: alpha(theme.palette.primary.main, 0.08),
              transform: 'translateY(-1px)',
            },
            backgroundColor: isOpen ? alpha(theme.palette.primary.main, 0.04) : 'transparent',
          }}
        >
          <ListItemIcon
            sx={{
              minWidth: { xs: 32, md: 36 },
              color: isOpen ? '#30c3b0' : alpha(theme.palette.text.primary, 0.7),
              transition: 'color 0.2s ease',
              '& svg': {
                fontSize: { xs: '1.3rem', md: '1.5rem' }
              }
            }}
          >
            {icon}
          </ListItemIcon>
          <ListItemText
            primary={title}
            primaryTypographyProps={{
              fontSize: { xs: 13.5, md: 14.5 },
              fontWeight: isOpen ? 700 : 600,
              color: isOpen ? '#30c3b0' : theme.palette.text.primary,
              textAlign: 'left'
            }}
            sx={{ transition: 'color 0.2s ease, font-weight 0.2s ease' }}
          />
          {badgeCount && (
            <Badge
              badgeContent={badgeCount}
              color="success"
              sx={{
                ml: 1,
                '& .MuiBadge-badge': {
                  fontSize: '0.7rem',
                  height: 20,
                  minWidth: 20,
                  borderRadius: '50%'
                }
              }}
            />
          )}
          {isOpen ? (
            <ExpandLessIcon
              fontSize="small"
              sx={{
                transition: 'transform 0.3s ease',
                transform: 'rotate(0deg)',
                color: '#30c3b0'
              }}
            />
          ) : (
            <ExpandMoreIcon
              fontSize="small"
              sx={{
                transition: 'transform 0.3s ease',
                transform: 'rotate(0deg)',
              }}
            />
          )}
        </ListItemButton>
      </StyledListItem>
      
      <Collapse
        in={isOpen}
        timeout="auto"
        unmountOnExit
        sx={{
          transition: 'all 0.3s ease-in-out',
        }}
      >
        <List
          component="div"
          disablePadding
          sx={{
            pl: { xs: 0.5, md: 1 },
            pr: { xs: 0.3, md: 0.5 },
            position: 'relative',
            '&::before': {
              content: '""',
              position: 'absolute',
              top: 5,
              bottom: 5,
              left: { xs: 8, md: 12 },
              width: 2,
              backgroundColor: alpha(theme.palette.primary.main, 0.1),
              borderRadius: 4
            }
          }}
        >
          {children}
        </List>
      </Collapse>
    </>
  );
};

const SidebarMenu = () => {
  const theme = useTheme();
  const { hasPermission } = usePermissions();
  const { hasInvoicePagePermission, hasInvoiceSettingPermission } = useInvoicePermissions();
  
  // التحقق مما إذا كان المستخدم مدير نظام
  const userInfoStr = localStorage.getItem('user');
  const userInfo = userInfoStr ? JSON.parse(userInfoStr) : null;
  const isSystemAdmin = userInfo?.isSystemAdmin;
  
  // طباعة تشخيصية
  console.log('[SidebarMenu] حالة مدير النظام:', isSystemAdmin);
  const { hasVoucherTemplatePermission, hasVoucherSettingPermission } = useSpecializedVoucherPermissions();
  const [invoiceMenuItems, setInvoiceMenuItems] = useState<Array<{
    id: string,
    to: string,
    icon: React.ReactNode,
    text: string,
    permissionCheck?: {
      section: string,
      page: string,
      action: string
    }
  }>>([]);
  const [loadingInvoiceMenuItems, setLoadingInvoiceMenuItems] = useState<boolean>(true);
  
  // إضافة حالة لعناصر قائمة السندات المتخصصة
  const [voucherMenuItems, setVoucherMenuItems] = useState<Array<{
    id: string,
    to: string,
    icon: React.ReactNode,
    text: string,
    permissionCheck?: {
      section: string,
      page: string,
      action: string
    }
  }>>([]);
  const [loadingVoucherMenuItems, setLoadingVoucherMenuItems] = useState<boolean>(true);
  
  // تحميل عناصر قائمة الفواتير عند تحميل المكون
  useEffect(() => {
    const loadInvoiceMenuItems = async () => {
      try {
        setLoadingInvoiceMenuItems(true);
        const items = await getInvoiceMenuItems();
        // تصفية العناصر التي لديها معرف غير محدد
        const validItems = items.filter(item => item.id) as Array<{id: string, to: string, icon: React.ReactNode, text: string}>;
        setInvoiceMenuItems(validItems);
      } catch (error) {
        console.error('فشل في تحميل عناصر قائمة الفواتير:', error);
      } finally {
        setLoadingInvoiceMenuItems(false);
      }
    };
    
    loadInvoiceMenuItems();
    
    // تحميل عناصر قائمة السندات المتخصصة
    const loadVoucherMenuItems = async () => {
      try {
        setLoadingVoucherMenuItems(true);
        const items = await getSpecializedVoucherMenuItems();
        // تصفية العناصر التي لديها معرف غير محدد
        const validItems = items.filter(item => item.id) as Array<{
          id: string,
          to: string,
          icon: React.ReactNode,
          text: string,
          permissionCheck?: {
            section: string;
            page: string;
            action: string;
          }
        }>;
        setVoucherMenuItems(validItems);
      } catch (error) {
        console.error('فشل في تحميل عناصر قائمة السندات المتخصصة:', error);
      } finally {
        setLoadingVoucherMenuItems(false);
      }
    };
    
    loadVoucherMenuItems();
  }, []);

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        height: '100%',
        overflowX: 'hidden',
        overflowY: 'auto',
        // تحسين التمرير على الجوال
        WebkitOverflowScrolling: 'touch',
        '&::-webkit-scrollbar': {
          width: '6px',
        },
        '&::-webkit-scrollbar-track': {
          background: 'transparent',
        },
        '&::-webkit-scrollbar-thumb': {
          background: alpha(theme.palette.primary.main, 0.1),
          borderRadius: '10px',
        },
        '&::-webkit-scrollbar-thumb:hover': {
          background: alpha(theme.palette.primary.main, 0.2),
        }
      }}
    >
      {/* Dashboard Button - تحسين للجوال */}
      <Box sx={{ px: { xs: 1.5, md: 2 } }}>
        <Tooltip title="لوحة التحكم" placement="left">
          <Button
            variant="contained"
            fullWidth
            component={NavLink}
            to="/"
            startIcon={<DashboardIcon sx={{ fontSize: { xs: '1.2rem', md: '1.5rem' } }} />}
            sx={{
              py: { xs: 1, md: 1.2 },
              px: { xs: 1.5, md: 2 },
              backgroundColor: '#30c3b0',
              color: 'white',
              '&:hover': {
                backgroundColor: '#25a899',
                transform: 'translateY(-2px)'
              },
              mb: 1,
              mt: 1,
              boxShadow: '0 4px 12px rgba(48, 195, 176, 0.3)',
              borderRadius: 2,
              fontWeight: 600,
              fontSize: { xs: '0.875rem', md: '1rem' },
              transition: 'all 0.2s ease',
              textAlign: 'left',
              justifyContent: 'flex-start'
            }}
          >
            لوحة التحكم
          </Button>
        </Tooltip>
      </Box>
      
      <Divider
        sx={{
          my: 2,
          mx: 3,
          opacity: 0.6,
          borderColor: alpha(theme.palette.divider, 0.08),
          boxShadow: `0 1px 2px ${alpha(theme.palette.primary.main, 0.03)}`
        }}
      />
      
      {/* Main Navigation - تحسين للجوال */}
      <List component="nav" sx={{ px: { xs: 1, md: 1.5 }, width: '100%' }}>
        {/* Contacts Section */}
        <CollapsibleSection
          title="جهات الاتصال"
          icon={<PeopleIcon />}
          defaultOpen={true}
          badgeCount={6}
          sectionId="contacts"
          hideIfNoPermission={true}
        >
          <MenuItem
            to="/contacts"
            icon={<PeopleIcon fontSize="small" />}
            text="جميع جهات الاتصال"
            sectionId="contacts"
            pageId="all"
            actionId="view"
            hideIfNoPermission={true}
          />
          <MenuItem
            to="/contacts/customers"
            icon={<PersonIcon fontSize="small" />}
            text="العملاء"
            badge={5}
            sectionId="contacts"
            pageId="customers"
            actionId="view_page"
            hideIfNoPermission={true}
          />
          <MenuItem
            to="/contacts/suppliers"
            icon={<SupplierIcon fontSize="small" />}
            text="الموردين"
            sectionId="contacts"
            pageId="suppliers"
            actionId="view_page"
            hideIfNoPermission={true}
          />
          <MenuItem
            to="/contacts/linked-accounts"
            icon={<AccountBalanceIcon fontSize="small" />}
            text="إعدادات الحسابات المرتبطة"
            sectionId="contacts"
            pageId="linked-accounts"
            actionId="view"
            hideIfNoPermission={true}
          />
          <MenuItem
            to="/contacts/import"
            icon={<ImportExportIcon fontSize="small" />}
            text="استيراد العملاء والموردين"
            sectionId="contacts"
            pageId="import"
            actionId="view"
            hideIfNoPermission={true}
          />
          <MenuItem
            to="/contacts/loyalty-points"
            icon={<StarsIcon fontSize="small" />}
            text="نقاط الولاء"
            sectionId="contacts"
            pageId="loyalty-points"
            actionId="view_page"
            hideIfNoPermission={true}
          />
          <MenuItem
            to="/contacts/all-loyalty-points"
            icon={<TrendingUpIcon fontSize="small" />}
            text="نقاط الولاء للعملاء"
            sectionId="contacts"
            pageId="all-loyalty-points"
            actionId="view_page"
            hideIfNoPermission={true}
          />
        </CollapsibleSection>
        
        {/* Products Section */}
        <CollapsibleSection
          title="المنتجات"
          icon={<InventoryIcon />}
          sectionId="products"
          hideIfNoPermission={true}
        >
          <MenuItem
            to="/products"
            icon={<ListIcon fontSize="small" />}
            text="قائمة المنتجات"
            sectionId="products"
            pageId="all"
            actionId="view_page"
            hideIfNoPermission={true}
          />
          <MenuItem
            to="/categories"
            icon={<CategoryIcon fontSize="small" />}
            text="التصنيفات"
            sectionId="products"
            pageId="categories"
            actionId="view_page"
            hideIfNoPermission={true}
          />
          <MenuItem
            to="/products/properties"
            icon={<StyleIcon fontSize="small" />}
            text="خصائص المنتج"
            sectionId="products"
            pageId="attributes"
            actionId="view_page"
            hideIfNoPermission={true}
          />
          <MenuItem
            to="/products/service-products"
            icon={<ReceiptIcon fontSize="small" />}
            text="المنتجات الخدمية"
            sectionId="products"
            pageId="service-products"
            actionId="view_page"
            hideIfNoPermission={true}
          />
          <MenuItem
            to="/products/service-operations"
            icon={<DescriptionIcon fontSize="small" />}
            text="عمليات المنتجات الخدمية"
            sectionId="products"
            pageId="service-product-operations"
            actionId="view_page"
            hideIfNoPermission={true}
          />
          <MenuItem
            to="/products/new"
            icon={<AddIcon fontSize="small" />}
            text="إضافة منتج جديد"
            sectionId="products"
            pageId="add-new"
            actionId="view_page"
            hideIfNoPermission={true}
          />
          <MenuItem
            to="/products/import"
            icon={<ImportExportIcon fontSize="small" />}
            text="استيراد المنتجات"
            sectionId="products"
            pageId="import"
            actionId="view_page"
            hideIfNoPermission={true}
          />
          <MenuItem
            to="/products/bulk-update"
            icon={<ImportExportIcon fontSize="small" />}
            text="تحديث المنتجات"
            sectionId="products"
            pageId="bulk-update"
            actionId="view_page"
            hideIfNoPermission={true}
          />
          <MenuItem
            to="/units"
            icon={<StraightenOutlined fontSize="small" />}
            text="الوحدات"
            sectionId="products"
            pageId="units"
            actionId="view_page"
            hideIfNoPermission={true}
          />
          <MenuItem
            to="/products/reorder-quantities"
            icon={<RefreshIcon fontSize="small" />}
            text="كميات إعادة الطلب"
            sectionId="products"
            pageId="reorder-quantities"
            actionId="view_page"
            hideIfNoPermission={true}
          />
          <MenuItem
            to="/products/related-products"
            icon={<SwapHorizIcon fontSize="small" />}
            text="المنتجات المرتبطة"
            sectionId="products"
            pageId="related-products"
            actionId="view_page"
            hideIfNoPermission={true}
          />
        </CollapsibleSection>
        
        {/* Marketing Section */}
        <CollapsibleSection
          title="التسويق"
          icon={<CampaignIcon />}
          defaultOpen={true}
          badgeCount={3}
          sectionId="marketing"
          hideIfNoPermission={true}
        >
          <MenuItem
            to="/marketing"
            icon={<DashboardIcon fontSize="small" />}
            text="لوحة التسويق"
            sectionId="marketing"
            pageId="dashboard"
            actionId="view_page"
            hideIfNoPermission={true}
          />
          <MenuItem
            to="/marketing/campaigns"
            icon={<OfferIcon fontSize="small" />}
            text="الحملات التسويقية"
            badge={3}
            sectionId="marketing"
            pageId="campaigns"
            actionId="view_page"
            hideIfNoPermission={true}
          />
          <MenuItem
            to="/marketing/campaigns/new"
            icon={<AddCampaignIcon fontSize="small" />}
            text="إنشاء حملة جديدة"
            sectionId="marketing"
            pageId="new-campaign"
            actionId="view_page"
            hideIfNoPermission={true}
          />
           <MenuItem
             to="/marketing/coupons"
             icon={<CouponIcon fontSize="small" />}
             text="الكوبونات"
             sectionId="marketing"
             pageId="coupons"
             actionId="view_page"
             hideIfNoPermission={true}
           />
           <MenuItem
             to="/marketing/coupon-storage"
             icon={<ReceiptIcon fontSize="small" />}
             text="تتبع الكوبونات"
             sectionId="marketing"
             pageId="coupon-storage"
             actionId="view_page"
             hideIfNoPermission={true}
           />
           <MenuItem
             to="/marketing/sms/send"
             icon={<SendIcon fontSize="small" />}
             text="إرسال رسائل SMS"
             sectionId="marketing"
             pageId="sms-send"
             actionId="view_page"
             hideIfNoPermission={true}
           />
          <MenuItem
            to="/marketing/sms/history"
            icon={<HistoryIcon fontSize="small" />}
            text="سجل رسائل SMS"
            sectionId="marketing"
            pageId="sms-history"
            actionId="view_page"
            hideIfNoPermission={true}
          />
          <MenuItem
            to="/marketing/sms/providers"
            icon={<BusinessIcon fontSize="small" />}
            text="مزودو خدمة SMS"
            sectionId="marketing"
            pageId="sms-providers"
            actionId="view_page"
            hideIfNoPermission={true}
          />
          
          <MenuItem
            to="/marketing/whatsapp/providers"
            icon={<WhatsAppIcon fontSize="small" />}
            text="مزودو واتساب"
            sectionId="marketing"
            pageId="whatsapp-providers"
            actionId="view_page"
            hideIfNoPermission={true}
          />
          <MenuItem
            to="/marketing/whatsapp/send"
            icon={<SendIcon fontSize="small" />}
            text="إرسال رسائل واتساب"
            sectionId="marketing"
            pageId="whatsapp-send"
            actionId="view_page"
            hideIfNoPermission={true}
          />
          <MenuItem
            to="/marketing/whatsapp/campaigns"
            icon={<CampaignIcon fontSize="small" />}
            text="قائمة الحملات المرسلة"
            sectionId="marketing"
            pageId="whatsapp-campaigns"
            actionId="view_page"
            hideIfNoPermission={true}
          />
          <MenuItem
            to="/marketing/whatsapp/history"
            icon={<HistoryIcon fontSize="small" />}
            text="سجل رسائل واتساب"
            sectionId="marketing"
            pageId="whatsapp-history"
            actionId="view_page"
            hideIfNoPermission={true}
          />
          <MenuItem
            to="/marketing/whatsapp/analytics"
            icon={<AnalyticsIcon fontSize="small" />}
            text="إحصائيات واتساب"
            sectionId="marketing"
            pageId="whatsapp-analytics"
            actionId="view_page"
            hideIfNoPermission={true}
          />
        </CollapsibleSection>
        
        {/* Warehouses Section */}
        <CollapsibleSection
          title="المستودعات"
          icon={<WarehouseIcon />}
          defaultOpen={true}
          sectionId="warehouses"
          hideIfNoPermission={true}
        >
          <MenuItem
            to="/warehouses/list"
            icon={<ListIcon fontSize="small" />}
            text="قائمة المستودعات"
            sectionId="warehouses"
            pageId="list"
            actionId="view_page"
            hideIfNoPermission={true}
          />
          <MenuItem
            to="/warehouses/transfer-list"
            icon={<SwapHorizIcon fontSize="small" />}
            text="قائمة التحويلات"
            sectionId="warehouses"
            pageId="transfers"
            actionId="view_page"
            hideIfNoPermission={true}
          />
          <MenuItem
            to="/warehouses/transfer"
            icon={<AddIcon fontSize="small" />}
            text="إضافة تحويل جديد"
            sectionId="warehouses"
            pageId="new-transfer"
            actionId="view_page"
            hideIfNoPermission={true}
          />
          <MenuItem
            to="/warehouses/inventory-events"
            icon={<EventIcon fontSize="small" />}
            text="أحداث معاملات المخزون"
            sectionId="warehouses"
            pageId="inventory-events"
            actionId="view_page"
            hideIfNoPermission={true}
          />
        </CollapsibleSection>
        
        {/* Inventory Count Section */}
        <CollapsibleSection
          title="جرد المستودعات"
          icon={<AssignmentTurnedInIcon />}
          sectionId="inventory-count"
          hideIfNoPermission={true}
        >
          <MenuItem
            to="/inventory-count"
            icon={<ListIcon fontSize="small" />}
            text="قائمة طلبات الجرد"
            sectionId="inventory-count"
            pageId="list"
            actionId="view_page"
            hideIfNoPermission={true}
          />
          <MenuItem
            to="/inventory-count/new"
            icon={<AddIcon fontSize="small" />}
            text="إنشاء طلب جرد جديد"
            sectionId="inventory-count"
            pageId="new-request"
            actionId="view_page"
            hideIfNoPermission={true}
          />
        </CollapsibleSection>
        
        {/* Warehouse Transfer Requests Section */}
        <CollapsibleSection
          title="طلبات تحويل بين المستودعات"
          icon={<SwapHorizIcon />}
          sectionId="warehouse-transfer-requests"
          hideIfNoPermission={true}
        >
          <MenuItem
            to="/warehouse-transfers/requests"
            icon={<ListIcon fontSize="small" />}
            text="قائمة الطلبات"
            sectionId="warehouse-transfer-requests"
            pageId="list"
            actionId="view_page"
            hideIfNoPermission={true}
          />
          <MenuItem
            to="/warehouse-transfers/requests/new"
            icon={<AddIcon fontSize="small" />}
            text="طلب تحويل جديد"
            sectionId="warehouse-transfer-requests"
            pageId="new-request"
            actionId="view_page"
            hideIfNoPermission={true}
          />
        </CollapsibleSection>
        
        {/* Invoices Section */}
        <CollapsibleSection
          title="الفواتير"
          icon={<ReceiptLongIcon />}
          defaultOpen={true}
          badgeCount={invoiceMenuItems.length}
          sectionId="invoices"
          hideIfNoPermission={true}
        >
          {/* عرض صفحات الفواتير بشكل ديناميكي */}
          {loadingInvoiceMenuItems ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', p: 2 }}>
              <CircularProgress size={24} />
            </Box>
          ) : (
            invoiceMenuItems.map(item => (
              <MenuItem
                key={item.id}
                to={item.to}
                icon={item.icon}
                text={item.text}
                permissionCheck={item.permissionCheck}
              />
            ))
          )}
          
          {/* قيود الفواتير */}
          <MenuItem
            to="/invoices/entries"
            icon={<AccountBalanceIcon fontSize="small" />}
            text="قيود الفواتير"
            sectionId="invoices"
            pageId="invoice-entries"
            actionId="view_page"
            hideIfNoPermission={true}
          />

          {/* WooCommerce Integration */}
          <MenuItem
            to="/invoices/woocommerce/orders"
            icon={<ShoppingCartIcon fontSize="small" />}
            text="طلبات WooCommerce"
            sectionId="invoices"
            pageId="woocommerce-orders"
            actionId="view_page"
            hideIfNoPermission={true}
          />

          <MenuItem
            to="/invoices/woocommerce/settings"
            icon={<SettingsIcon fontSize="small" />}
            text="إعدادات WooCommerce"
            sectionId="invoices"
            pageId="woocommerce-settings"
            actionId="view_page"
            hideIfNoPermission={true}
          />
        </CollapsibleSection>
        
        {/* Financial Accounts Section */}
        <CollapsibleSection
          title="الحسابات المالية"
          icon={<AccountBalanceIcon />}
          defaultOpen={true}
          sectionId="finance"
          hideIfNoPermission={true}
        >
          <MenuItem
            to="/finance/accounts"
            icon={<AttachMoneyIcon fontSize="small" />}
            text="إدارة الحسابات العامة"
            sectionId="finance"
            pageId="accounts-management"
            actionId="view_page"
            hideIfNoPermission={true}
          />
          <MenuItem
            to="/finance/account-statements"
            icon={<LibraryBooksIcon fontSize="small" />}
            text="كشف حساب"
            sectionId="finance"
            pageId="account-statements"
            actionId="view_page"
            hideIfNoPermission={true}
          />
          <MenuItem
            to="/finance/ledger"
            icon={<LibraryBooksIcon fontSize="small" />}
            text="دفتر الأستاذ العام"
            sectionId="finance"
            pageId="general-ledger"
            actionId="view_page"
            hideIfNoPermission={true}
          />
          <MenuItem
            to="/finance/receipts"
            icon={<PaidIcon fontSize="small" />}
            text="سندات القبض"
            sectionId="finance"
            pageId="receipt-vouchers"
            actionId="view_page"
            hideIfNoPermission={true}
          />
          <MenuItem
            to="/finance/payments"
            icon={<PaymentIcon fontSize="small" />}
            text="سندات الصرف"
            sectionId="finance"
            pageId="payment-vouchers"
            actionId="view_page"
            hideIfNoPermission={true}
          />
          
          {/* إضافة قائمة السندات المتخصصة - جزء جديد */}
          <CollapsibleSection
            title="السندات المتخصصة"
            icon={<DescriptionIcon />}
            defaultOpen={false}
            badgeCount={voucherMenuItems.length}
          >
            {/* عرض صفحات السندات المتخصصة بشكل ديناميكي */}
            {loadingVoucherMenuItems ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', p: 2 }}>
                <CircularProgress size={24} />
              </Box>
            ) : (
              voucherMenuItems
                .filter(item => {
                  // تصفية العناصر بناءً على الصلاحيات
                  if (!item.permissionCheck) return true;
                  
                  const { section, page, action } = item.permissionCheck;
                  
                  // استخدام التحقق من الصلاحيات
                  return hasPermission(section, page, action);
                })
                .map(item => (
                  <MenuItem
                    key={item.id}
                    to={item.to}
                    icon={item.icon}
                    text={item.text}
                  />
                ))
            )}
            
            {/* إضافة رابط لإدارة السندات المتخصصة */}
            <MenuItem
              to="/finance/specialized-vouchers"
              icon={<SettingsIcon fontSize="small" />}
              text="إدارة السندات المتخصصة"
              sectionId="finance"
              pageId="specialized-voucher-templates"
              actionId="view_page"
              hideIfNoPermission={true}
            />
          </CollapsibleSection>
          
          {/* إضافة رابط لمجموعات الرسوم */}
          <MenuItem
            to="/finance/fee-groups"
            icon={<LocalAtmIcon fontSize="small" />}
            text="مجموعات الرسوم"
            sectionId="finance"
            pageId="fee-groups"
            actionId="view_page"
            hideIfNoPermission={true}
          />
          
          <MenuItem
            to="/finance/reports"
            icon={<BarChartIcon fontSize="small" />}
            text="التقارير المالية"
            sectionId="finance"
            pageId="financial-reports"
            actionId="view_page"
            hideIfNoPermission={true}
          />
          <MenuItem
            to="/finance/transactions"
            icon={<LocalAtmIcon fontSize="small" />}
            text="إدارة الأرصدة والمعاملات"
            sectionId="finance"
            pageId="balance-management"
            actionId="view_page"
            hideIfNoPermission={true}
          />
          <MenuItem
            to="/finance/export-import"
            icon={<ImportExportIcon fontSize="small" />}
            text="التصدير والاستيراد"
            sectionId="finance"
            pageId="export-import"
            actionId="view_page"
            hideIfNoPermission={true}
          />
        </CollapsibleSection>
        
        {/* Reports Section */}
        <CollapsibleSection
          title="التقارير"
          icon={<AssessmentIcon />}
          sectionId="reports"
          hideIfNoPermission={true}
        >
          <MenuItem
            to="/reports/inventory"
            icon={<InventoryIcon fontSize="small" />}
            text="المخزون"
            sectionId="reports"
            pageId="inventory"
            actionId="view_page"
            hideIfNoPermission={true}
          />
          <MenuItem
            to="/reports/inventory-display"
            icon={<VisibilityIcon fontSize="small" />}
            text="عرض كميات المخزون"
            sectionId="reports"
            pageId="inventory-display-report"
            actionId="view_page"
            hideIfNoPermission={true}
          />
          <MenuItem
            to="/reports/inventory-movement"
            icon={<SwapHorizIcon fontSize="small" />}
            text="حركة المخزون"
            sectionId="reports"
            pageId="inventory-movement"
            actionId="view_page"
            hideIfNoPermission={true}
          />
          <MenuItem
            to="/reports/product-movement"
            icon={<TrendingUpIcon fontSize="small" />}
            text="تقرير حركة المنتجات"
            sectionId="reports"
            pageId="product-movement-report"
            actionId="view_page"
            hideIfNoPermission={true}
          />
          <MenuItem
            to="/reports/product-profit"
            icon={<AttachMoneyIcon fontSize="small" />}
            text="تقرير ربح المنتجات"
            sectionId="reports"
            pageId="product-profit-report"
            actionId="view_page"
            hideIfNoPermission={true}
          />
          <MenuItem
            to="/reports/product-prices"
            icon={<AttachMoneyIcon fontSize="small" />}
            text="تقرير أسعار المنتجات"
            sectionId="reports"
            pageId="product-prices"
            actionId="view_page"
            hideIfNoPermission={true}
          />
          <MenuItem
            to="/reports/category-quantities"
            icon={<CategoryIcon fontSize="small" />}
            text="عرض كميات التصنيفات"
            sectionId="reports"
            pageId="category-quantities-report"
            actionId="view_page"
            hideIfNoPermission={true}
          />
          <MenuItem
            to="/reports/reorder-quantities"
            icon={<RefreshIcon fontSize="small" />}
            text="تقرير كميات إعادة الطلب"
            sectionId="reports"
            pageId="reorder-quantities-report"
            actionId="view_page"
            hideIfNoPermission={true}
          />
          <MenuItem
            to="/reports/zatca-invoices"
            icon={<SendIcon fontSize="small" />}
            text="فواتير إرسال للهيئة"
            sectionId="reports"
            pageId="zatca-invoice-report"
            actionId="view_page"
            hideIfNoPermission={true}
          />
          <MenuItem
            to="/reports/payment-receipt-vouchers"
            icon={<ReceiptIcon fontSize="small" />}
            text="تقرير سندات القبض والصرف"
            sectionId="reports"
            pageId="payment-receipt-vouchers-report"
            actionId="view_page"
            hideIfNoPermission={true}
          />
          <MenuItem
            to="/reports/specialized-vouchers"
            icon={<DescriptionIcon fontSize="small" />}
            text="تقرير السندات المتخصصة"
            sectionId="reports"
            pageId="specialized-vouchers-report"
            actionId="view_page"
            hideIfNoPermission={true}
          />
          <MenuItem
            to="/reports/commissions"
            icon={<AttachMoneyIcon fontSize="small" />}
            text="تقرير العمولات البيعية"
            sectionId="reports"
            pageId="sales-commissions"
            actionId="view_page"
            hideIfNoPermission={true}
          />
        </CollapsibleSection>
        
        {/* Settings Section */}
        <CollapsibleSection
          title="الإعدادات"
          icon={<SettingsIcon />}
          sectionId="settings"
          hideIfNoPermission={true}
        >
          <MenuItem
            to="/settings/branches"
            icon={<BusinessIcon fontSize="small" />}
            text="الفروع"
            sectionId="settings"
            pageId="branches"
            actionId="view_page"
            hideIfNoPermission={true}
          />
          <MenuItem
            to="/settings/taxes"
            icon={<ReceiptIcon fontSize="small" />}
            text="الضرائب"
            sectionId="settings"
            pageId="taxes"
            actionId="view_page"
            hideIfNoPermission={true}
          />
          <MenuItem
            to="/settings/zatca-certificates"
            icon={<SecurityIcon fontSize="small" />}
            text="شهادات فاتورة"
            sectionId="settings"
            pageId="zatca-certificates"
            actionId="view_page"
            hideIfNoPermission={true}
          />
          <MenuItem
            to="/settings/document-alerts"
            icon={<NotificationsIcon fontSize="small" />}
            text="التنبيهات"
            sectionId="settings"
            pageId="document_alerts"
            actionId="view_page"
            hideIfNoPermission={true}
          />
          <MenuItem
            to="/settings/advanced"
            icon={<AdminPanelSettingsIcon fontSize="small" />}
            text="الإعدادات المتقدمة"
            sectionId="settings"
            pageId="advanced-settings"
            actionId="view_page"
            hideIfNoPermission={true}
          />
          {!isSystemAdmin && (
            <MenuItem
              to="/settings/dashboard-widgets"
              icon={<DashboardIcon fontSize="small" />}
              text="إعدادات الداش بورد"
              sectionId="settings"
              pageId="dashboard-widgets"
              actionId="view_page"
              hideIfNoPermission={true}
            />
          )}
          {isSystemAdmin && (
            <MenuItem
              to="/settings/admin/user-widgets"
              icon={<PeopleIcon fontSize="small" />}
              text="إدارة ويدجات المستخدمين"
              sectionId="settings"
              pageId="admin-user-widgets"
              actionId="view_page"
            />
          )}
        </CollapsibleSection>
        
        {/* Admin Section - Set to defaultOpen=true */}
        <CollapsibleSection
          title="المشرفين"
          icon={<AdminIcon />}
          defaultOpen={true}
          sectionId="admin"
          hideIfNoPermission={true}
        >
          <MenuItem
            to="/users"
            icon={<PeopleIcon fontSize="small" />}
            text="المستخدمين"
            sectionId="admin"
            pageId="users"
            actionId="view_page"
            hideIfNoPermission={true}
          />
          <MenuItem
            to="/admin/user-groups"
            icon={<GroupIcon fontSize="small" />}
            text="مجموعات الصلاحيات"
            sectionId="admin"
            pageId="user-groups"
            actionId="view_page"
            hideIfNoPermission={true}
          />
        </CollapsibleSection>
      </List>
      
      {/* Footer */}
      <Box
        sx={{
          mt: 'auto',
          p: 2,
          textAlign: 'left',
          borderTop: `1px solid ${alpha(theme.palette.divider, 0.06)}`,
          marginTop: 4
        }}
      >
        <Typography
          variant="caption"
          color="text.secondary"
          sx={{
            fontSize: '0.7rem',
            opacity: 0.7,
            display: 'block'
          }}
        >
          نظام ERP
        </Typography>
        <Typography
          variant="caption"
          color="text.secondary"
          sx={{
            fontSize: '0.7rem',
            opacity: 0.5
          }}
        >
          الإصدار 1.0.0
        </Typography>
      </Box>
    </Box>
  );
};

export default SidebarMenu;